import { z } from 'zod';
import { insightSchema } from './materials';
import { gapEvidenceCoverage, gapFactorProgress, type GapFactor, type GapIndicator, type Gaps } from './strategy';

/* ─────────────────────────────────────────────────────────────────────
   Mapa de Gaps — o modelo inicial, as contas do consolidado e a leitura
   do agente. As contas são puras: a tela as refaz a cada tecla, o
   servidor as refaz para o agente. A leitura (maturidade real, análise,
   recomendações) é do agente e se atualiza sozinha quando o mapa muda.
   ───────────────────────────────────────────────────────────────────── */

type Seed = [name: string, description: string];

const INOVACAO: Seed[] = [
  ['Tese de Inovação', 'Existe uma tese clara de onde e por que inovar, que orienta as decisões.'],
  ['Modelagem de Negócios', 'O modelo de negócio é revisado com método e números.'],
  ['Experiência do Cliente', 'A jornada do cliente é mapeada, medida e melhorada.'],
  ['Produtos Digitais', 'Há produtos ou serviços digitais que ampliam acesso e engajamento.'],
  ['Inovação no Produto ou Serviço', 'A oferta evolui com pilotos, testes e aprendizado.'],
  ['Diferenciais Competitivos', 'Os diferenciais são conhecidos, comprovados e comunicados.'],
  ['Inteligência Artificial', 'IA é usada em processos e na oferta, com avaliação de impacto.'],
];
const PEDAGOGICOS: Seed[] = [
  ['Currículo por Competências', 'O currículo é organizado por competências, com progressão clara.'],
  ['Formação Docente', 'Há um programa contínuo de formação e acompanhamento dos professores.'],
  ['Avaliação da Aprendizagem', 'A avaliação é formativa e orienta o ensino ao longo do período.'],
  ['Projetos Interdisciplinares', 'Os projetos integram disciplinas em torno de problemas reais.'],
  ['Inclusão e Diversidade', 'Cada aluno tem o apoio de que precisa para aprender no seu ritmo.'],
  ['Desenvolvimento Socioemocional', 'Competências socioemocionais fazem parte da rotina.'],
];
const OPERACAO: Seed[] = [
  ['Qualidade da Entrega', 'O produto ou serviço é entregue com padrão e consistência.'],
  ['Atendimento ao Cliente', 'O atendimento tem canais, prazos e satisfação medidos.'],
  ['Cadeia de Suprimentos', 'Fornecedores e insumos são geridos com previsibilidade.'],
  ['Capacidade Operacional', 'A operação comporta o crescimento previsto.'],
  ['Padronização e Qualidade', 'Processos críticos são documentados e auditados.'],
  ['Gestão do Conhecimento', 'O que a organização aprende fica registrado e acessível.'],
];
const MERCADO: Seed[] = [
  ['Análise de Mercado', 'Mercado, concorrência e tendências são acompanhados com dados.'],
  ['Portfólio de Diferenciais', 'Os diferenciais estão definidos e presentes na comunicação.'],
  ['Marketing Digital', 'Conteúdo, mídia e canais digitais geram alcance e oportunidades.'],
  ['Máquina de Vendas', 'O funil de captação tem etapas, metas e acompanhamento.'],
  ['Reputação', 'Satisfação, indicação e presença pública são monitoradas.'],
  ['Branding', 'A identidade da marca é aplicada com consistência.'],
];
const ADMIN: Seed[] = [
  ['Gestão de Pessoas', 'Recrutamento, desenvolvimento e avaliação de desempenho.'],
  ['Gestão Tecnológica', 'Infraestrutura, sistemas e suporte de TI.'],
  ['Gestão de Processos', 'Mapeamento e melhoria contínua dos processos internos.'],
  ['Gestão Financeira', 'Planejamento, controle orçamentário e prestação de contas.'],
  ['Gestão Jurídica', 'Conformidade legal e gestão de contratos.'],
  ['Gestão de ESG', 'Ações ambientais, sociais e de governança.'],
  ['Gestão de Performance', 'Definição e acompanhamento de indicadores (KPIs).'],
];

const blank = (key: string, [name, description]: Seed, n: number): GapIndicator => ({
  id: `${key}-${n + 1}`, name, description, what: '', why: '', where: '', who: '', when: '', how: '',
  cost: null, evidence: 0, maturity: 0, target: 0,
});
const factor = (key: string, name: string, seeds: Seed[]): GapFactor => ({ key, name, indicators: seeds.map((s, n) => blank(key, s, n)) });

/** O mapa em branco, com os indicadores do ADS. Educação ganha os fatores pedagógicos; os demais, os de operação. */
export function gapsTemplate(segment = ''): Gaps {
  const edu = /educa/i.test(segment);
  return {
    factors: [
      factor('inovacao', 'Fatores Estratégicos de Inovação', INOVACAO),
      edu ? factor('pedagogicos', 'Fatores Pedagógicos', PEDAGOGICOS) : factor('operacao', 'Fatores de Operação e Entrega', OPERACAO),
      factor('mercadologicos', 'Fatores Mercadológicos', MERCADO),
      factor('administrativos', 'Fatores Administrativos', ADMIN),
    ],
  };
}

/* ── as contas do consolidado ─────────────────────────────────────── */

const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/\s+/g, ' ').trim();
/** "Ana Souza e Marcos Lima" → duas pessoas. */
export const splitNames = (s: string) =>
  s.split(/\s*(?:,|;|\/|\+|&|\be\b)\s*/i).map(x => x.trim()).filter(x => x.length > 1);

export interface Ranked { name: string; count: number; pct: number }
export interface GapsMetrics {
  total: number;
  factors: Array<{
    key: string; name: string; progress: number; indicators: number;
    withOwner: number; withEvidence: number; coverage: number; files: number;
    people: number; sectors: number;
    /** maturidade média declarada, 0–5, e em % (0–100) */
    maturity: number; declaredPct: number; target: number;
  }>;
  people: { total: number; gapsPerPerson: number; top: Ranked[]; others: Ranked | null };
  sectors: { total: number; actions: number; perFactor: number; top: Ranked[]; others: Ranked | null };
  owners: { with: number; pct: number };
  evidence: { with: number; pct: number; files: number };
  /** maiores distâncias entre a maturidade e a meta */
  biggest: Array<{ name: string; factor: string; maturity: number; target: number; gap: number }>;
}

function rank(counts: Map<string, { name: string; count: number }>, total: number, top = 5): { top: Ranked[]; others: Ranked | null } {
  const all = [...counts.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  const pct = (c: number) => (total ? Math.round((c / total) * 100) : 0);
  const head = all.slice(0, top).map(x => ({ ...x, pct: pct(x.count) }));
  const rest = all.slice(top).reduce((n, x) => n + x.count, 0);
  return { top: head, others: rest ? { name: 'Outros', count: rest, pct: pct(rest) } : null };
}

export function gapsMetrics(g: Gaps): GapsMetrics {
  const people = new Map<string, { name: string; count: number }>();
  const sectors = new Map<string, { name: string; count: number }>();
  let assignments = 0, actions = 0;
  const factors = g.factors.map(f => {
    const fPeople = new Set<string>(), fSectors = new Set<string>();
    for (const i of f.indicators) {
      for (const n of splitNames(i.who)) {
        const k = fold(n);
        fPeople.add(k);
        const cur = people.get(k) ?? { name: n, count: 0 };
        cur.count++; people.set(k, cur); assignments++;
      }
      const w = i.where.trim();
      if (w) {
        const k = fold(w);
        fSectors.add(k); actions++;
        const cur = sectors.get(k) ?? { name: w, count: 0 };
        cur.count++; sectors.set(k, cur);
      }
    }
    const rated = f.indicators.filter(i => i.maturity > 0);
    const maturity = rated.length ? rated.reduce((s, i) => s + i.maturity, 0) / rated.length : 0;
    const targeted = f.indicators.filter(i => i.target > 0);
    return {
      key: f.key, name: f.name, progress: gapFactorProgress(f), indicators: f.indicators.length,
      withOwner: f.indicators.filter(i => i.who.trim()).length,
      withEvidence: f.indicators.filter(i => i.evidence > 0).length,
      coverage: gapEvidenceCoverage(f),
      files: f.indicators.reduce((s, i) => s + i.evidence, 0),
      people: fPeople.size, sectors: fSectors.size,
      maturity, declaredPct: Math.round((maturity / 5) * 100),
      target: targeted.length ? targeted.reduce((s, i) => s + i.target, 0) / targeted.length : 0,
    };
  });
  const all = g.factors.flatMap(f => f.indicators.map(i => ({ ...i, factor: f.name })));
  const total = all.length;
  const withOwner = all.filter(i => i.who.trim()).length;
  const withEvidence = all.filter(i => i.evidence > 0).length;
  const pr = rank(people, assignments);
  const sr = rank(sectors, actions);
  const biggest = all.filter(i => i.maturity > 0)
    .map(i => ({ name: i.name, factor: i.factor, maturity: i.maturity, target: i.target || 5, gap: (i.target || 5) - i.maturity }))
    .filter(i => i.gap > 0)
    .sort((a, b) => b.gap - a.gap || a.maturity - b.maturity)
    .slice(0, 6);
  return {
    total, factors,
    people: { total: people.size, gapsPerPerson: people.size ? assignments / people.size : 0, ...pr },
    sectors: { total: sectors.size, actions, perFactor: factors.length ? factors.reduce((s, f) => s + f.sectors, 0) / factors.length : 0, ...sr },
    owners: { with: withOwner, pct: total ? Math.round((withOwner / total) * 100) : 0 },
    evidence: { with: withEvidence, pct: total ? Math.round((withEvidence / total) * 100) : 0, files: all.reduce((s, i) => s + i.evidence, 0) },
    biggest,
  };
}

/* ── a leitura do agente ──────────────────────────────────────────── */

export const gapsReadingSchema = z.object({
  /** a "Leitura da IA": os achados do consolidado, em um parágrafo */
  leitura: z.string().trim().min(20).max(900),
  /** recomendações de gestão, em ordem de prioridade */
  recomendacoes: z.array(z.string().trim().min(4).max(220)).min(1).max(5),
  /** a maturidade que as evidências sustentam, por fator (0–100) */
  maturidadeReal: z.array(z.object({
    factor: z.string().min(1).max(40),
    value: z.number().int().min(0).max(100),
    motivo: z.string().trim().max(240).default(''),
  })).max(8),
  insights: z.array(insightSchema).max(4).default([]),
});
export type GapsReading = z.infer<typeof gapsReadingSchema>;

/** A leitura de uma ferramenta do Strategy (Mapa de Gaps, Diferenciais…) e o estado dela. */
export interface ToolReadingDto<R> {
  reading: R | null;
  /** o mapa mudou depois desta leitura */
  stale: boolean;
  /** há uma leitura agendada ou em curso */
  pending: boolean;
  basic: boolean;
  note: string | null;
  updatedAt: string | null;
}
export type GapsReadingDto = ToolReadingDto<GapsReading>;
